import React from "react";
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import {Bar} from "react-chartjs-2";
import {Box, Skeleton} from "@mui/material";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, BarController, LineController, Title, Tooltip, Legend, PointElement);

const MixedChart = ({chartData, isLoading, showPercentage, yAxisLabel, height}) => {
    const groupLabels = ["Basic Cyber Security & Privacy Management", "Cyber Safety Management", "Cyber Civility"];

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    pointStyle: "rect",
                    font: {
                        size: 14,
                    },
                    color: "#333",
                    padding: 20,
                    pointStyleWidth: 70,
                    boxHeight: 20,
                    generateLabels: (chart) => {
                        let labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

                        const legendOrder = ["#003C69", "#0096C8", "#F05F64"];

                        labels.sort((a, b) => {
                            const colorA = a.fillStyle || a.strokeStyle || "";
                            const colorB = b.fillStyle || b.strokeStyle || "";
                            return legendOrder.indexOf(colorA) - legendOrder.indexOf(colorB);
                        });

                        return labels;
                    },

                },
                reverse: false,
            },

            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        const label = tooltipItems[0].label;
                        return Array.isArray(label) ? label.join(" ") : label.replace(/,/g, " ");
                    },
                },
            },

        },
        layout: {
            padding: {
                bottom: 40,
                top: 30,
            },
        },
        scales: {
            x: {
                stacked: true,
                afterFit: ((ctx) => {
                    ctx.height += 120;
                }),
                ticks: {
                    font: {
                        size: 12,
                    },
                    color: "#333",
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    padding: -110,
                    tickWidth: 3,
                    offset: 0,
                },
                grid: {
                    drawTicks: true,
                    lineWidth: (ctx) => {
                        const validDivider = ctx.index !== 0 && ctx.index % 5 === 0;
                        return yAxisLabel === "Cyber Citizenship Score" && validDivider ? 2 : 0;
                    },

                    color: "#ccc",
                    borderWidth: 2,
                    tickLength: 120,
                },
            },
            ...(yAxisLabel === "Cyber Citizenship Score" && {
                x2: {
                    position: "bottom",
                    grid: {
                        drawOnChartArea: false,
                        drawTicks: false,
                    },
                    ticks: {
                        font: {
                            size: 15,
                            weight: "bold"
                        },
                        color: "#333",
                        padding: 17,
                        autoSkip: false,
                        maxRotation: 0,
                        minRotation: 0,
                        callback: function (value, index) {
                            const groupSize = 5;
                            const middleIndex = Math.floor(groupSize / 2);
                            if (index % groupSize === middleIndex) {
                                let groupLabel = groupLabels[Math.floor(index / groupSize)];
                                return groupLabel ? groupLabel : "";
                            }
                            return "";
                        },
                    },
                },
            }),
            y: {
                stacked: true,
                ticks: {
                    font: {
                        size: 12,
                    },
                    color: "#333",
                    callback: (value) => (showPercentage ? `${value}%` : value),
                },
                grid: {
                    drawOnChartArea: false,
                },
                title: {
                    display: true,
                    text: yAxisLabel || "",
                    font: {
                        size: 14,
                    },
                    color: "#333",
                },
            },
        },
    };

    return (
        <Box>
            <Box
                sx={{
                    height: height || "400px",
                    minWidth: {xs: chartData.labels.length > 10 ? "1200px" : "600px", lg:chartData.labels.length > 12 ? "1200px" : "600px",},
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {isLoading ? (
                    <Skeleton
                        variant="rectangular"
                        width="80%"
                        height="80%"
                        sx={{
                            bgcolor: "grey.200",
                            borderRadius: "4px",
                        }}
                    />
                ) : chartData && chartData.labels && chartData.datasets.length > 0 ? (
                    <Bar data={chartData} options={options}/>
                ) : (
                    <h3>No Data Available</h3>
                )}
            </Box>
        </Box>
    );
};

export default MixedChart;
